'use client';

import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CtaFinal from '../homepage/components/CtaFinal';

function useReveal(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = ref?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [ref]);
}

const industries = [
  {
    tag: 'SaaS',
    title: 'B2B software',
    body: 'For SaaS teams with deal sizes under $15K, a $100K in-house hire never pencils, and post-raise teams need pipeline before headcount. A dedicated rep proves the ICP and the message, then the team scales with the motion instead of ahead of it.',
  },
  {
    tag: 'Services',
    title: 'Professional services',
    body: 'Legal, accounting, consulting, and agency buyers reply to well-targeted cold outreach at the highest rates of any vertical, and voice-led outreach consistently outperforms email here. Relationship businesses buy from conversations, not sequences.',
  },
  {
    tag: 'Finance',
    title: 'Financial services',
    body: 'Trust-driven sales with compliance-aware messaging. Phone-first prospecting outperforms in this vertical because credibility is established person to person, and our reps are trained to keep messaging inside your review standards.',
  },
  {
    tag: 'IT',
    title: 'IT and managed services',
    body: 'MSP and IT services buyers drown in automated email from your competitors. A rep who calls, speaks the buyer\'s language, and targets by region and stack cuts through where another sequence cannot.',
  },
  {
    tag: 'Health',
    title: 'Healthcare and medtech',
    body: 'Committee buying, long cycles, and cautious stakeholders. Phone conversations qualify real interest early and keep multi-month deals warm in a way no automated touch can.',
  },
  {
    tag: 'Industrial',
    title: 'Logistics and manufacturing',
    body: 'These buyers live on the phone and the floor, not in the inbox. Practical, direct calling with a clear value case matches how this market has always bought.',
  },
];

export default function IndustriesPage() {
  const sec1 = useRef<HTMLDivElement>(null);
  const sec2 = useRef<HTMLDivElement>(null);
  useReveal(sec1);
  useReveal(sec2);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <header
          className="py-24"
          style={{
            background:
              'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
          }}
        >
          <div className="max-w-site mx-auto px-7">
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              Industries served
            </div>
            <h1 className="heading-1 text-white">Where phone-first outbound wins</h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[62ch]">
              Cold outreach performance varies sharply by industry. We concentrate on B2B verticals where the data says a live conversation moves deals: markets where decision-makers still answer the phone and where buying decisions run on trust.
            </p>
          </div>
        </header>

        {/* Industries grid */}
        <section ref={sec1} className="py-24">
          <div className="max-w-site mx-auto px-7">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {industries.map((ind, i) => (
                <div
                  key={ind.tag}
                  className="rev bg-white border border-line-color rounded-brand p-7"
                  style={{ transitionDelay: `${i * 0.07}s` }}
                >
                  <span className="block font-mono text-[11px] tracking-[0.18em] text-primary uppercase mb-3.5">
                    {ind.tag}
                  </span>
                  <h3 className="heading-3 text-ink mb-3">{ind.title}</h3>
                  <p className="text-[15px] text-muted-text leading-relaxed">{ind.body}</p>
                </div>
              ))}
            </div>
            <div className="rev gold-strip mt-8" style={{ transitionDelay: '0.42s' }}>
              <div>
                <b className="text-primary">Why these six:</b> market data shows phone prospecting outperforming email in relationship-driven and regulated verticals, while services and legal buyers post the highest cold outreach response rates of any industry. We built the offering around where the evidence points.
              </div>
            </div>
          </div>
        </section>

        {/* Fit check */}
        <section ref={sec2} className="py-16 bg-white">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-13 max-w-[780px]">
              <div className="eyebrow">Fit check</div>
              <h2 className="heading-2 text-ink">You are a strong fit if</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ul className="rev list-check">
                <li>You sell B2B, to companies rather than consumers</li>
                <li>Your Ideal Customer Profile is defined, or you want help defining it</li>
              </ul>
              <ul className="rev list-check" style={{ transitionDelay: '0.1s' }}>
                <li>You have closers ready to take qualified meetings</li>
                <li>You sell into the US market during US business hours</li>
              </ul>
            </div>
            <p className="rev mt-6 text-muted-text" style={{ transitionDelay: '0.2s' }}>
              Selling in a different vertical is not a disqualifier. If your buyers answer the phone and your deal economics support outbound, we can likely build the motion. Bring it to a call and we will tell you straight.
            </p>
          </div>
        </section>

        <CtaFinal
          headline="See the playbook for your industry"
          body="A 30-minute call covers how we target, call, and message in your vertical."
          cta="Book a strategy call"
        />
      </main>
      <Footer />
    </>
  );
}
